import {
	Button,
	Dialog,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from "@trotroways/ui";
import { cn } from "@trotroways/utils";
import { memo, useCallback, useState } from "react";

const FARE_CHIPS = [1.5, 2.0, 2.5, 3.0] as const;

const REASONS = [
	{ value: "fuel", label: "Fuel price changed" },
	{ value: "route", label: "Route changed" },
	{ value: "wrong", label: "Wrong info" },
	{ value: "other", label: "Other" },
] as const;

interface FareCorrectionSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	currentFare: number;
	onConfirm: (amount: number, reason?: string) => Promise<boolean>;
}

export const FareCorrectionSheet = memo(function FareCorrectionSheet({
	open,
	onOpenChange,
	currentFare,
	onConfirm,
}: FareCorrectionSheetProps) {
	const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
	const [showReason, setShowReason] = useState(false);
	const [selectedReason, setSelectedReason] = useState<string | null>(null);
	const [customAmount, setCustomAmount] = useState("");
	const [status, setStatus] = useState<"idle" | "submitting" | "error" | "done">(
		"idle",
	);

	const handleChipClick = useCallback((amount: number) => {
		setSelectedAmount(amount);
		setCustomAmount("");
		setShowReason(true);
	}, []);

	const handleCustomChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			setCustomAmount(val);
			if (val) {
				setSelectedAmount(null);
			}
		},
		[],
	);

	const handleReasonClick = useCallback((value: string) => {
		setSelectedReason((prev) => (prev === value ? null : value));
	}, []);

	const handleSubmit = useCallback(async () => {
		const amount =
			selectedAmount ??
			(customAmount ? Number.parseFloat(customAmount) : currentFare);
		if (Number.isNaN(amount) || amount <= 0) return;
		setStatus("submitting");
		const ok = await onConfirm(amount, selectedReason ?? undefined);
		if (ok) {
			setStatus("done");
			onOpenChange(false);
		} else {
			setStatus("error");
		}
	}, [
		selectedAmount,
		customAmount,
		currentFare,
		selectedReason,
		onConfirm,
		onOpenChange,
	]);

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				setSelectedAmount(null);
				setShowReason(false);
				setSelectedReason(null);
				setCustomAmount("");
				setStatus("idle");
			}
			onOpenChange(open);
		},
		[onOpenChange],
	);

	const isValid =
		selectedAmount !== null ||
		(customAmount && Number.parseFloat(customAmount) > 0);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Update fare</DialogTitle>
				</DialogHeader>
				<div className="px-6 pb-6 space-y-4">
					<div className="flex gap-2 flex-wrap">
						{FARE_CHIPS.map((amount) => (
							<button
								key={amount}
								type="button"
								onClick={() => handleChipClick(amount)}
								className={cn(
									"px-4 py-2 rounded-full text-sm font-medium border transition-colors",
									selectedAmount === amount
										? "bg-primary text-primary-foreground border-primary"
										: "bg-muted text-foreground border-border hover:bg-accent",
								)}
							>
								¢{amount.toFixed(2)}
							</button>
						))}
					</div>

					<div className="relative">
						<span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
							¢
						</span>
						<input
							type="number"
							step="0.5"
							min="0.5"
							value={customAmount}
							onChange={handleCustomChange}
							placeholder="Custom amount"
							className="w-full rounded-lg border border-border bg-muted py-2 pl-7 pr-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary"
						/>
					</div>

					{showReason && (
						<div className="space-y-2">
							<p className="text-xs font-medium text-muted-foreground">
								Why?{" "}
								<span className="text-muted-foreground/50">(optional)</span>
							</p>
							<div className="flex flex-wrap gap-1.5">
								{REASONS.map((reason) => (
									<button
										key={reason.value}
										type="button"
										onClick={() => handleReasonClick(reason.value)}
										className={cn(
											"px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
											selectedReason === reason.value
												? "bg-primary/10 text-primary border-primary/30"
												: "bg-muted text-muted-foreground border-border hover:bg-accent",
										)}
									>
										{reason.label}
									</button>
								))}
							</div>
						</div>
					)}

					{status === "error" && (
						<p className="text-xs font-medium text-red-500">
							Something went wrong submitting your report. Please try again.
						</p>
					)}

					<Button
						type="button"
						className="w-full"
						disabled={!isValid || status === "submitting"}
						onClick={handleSubmit}
					>
						{status === "submitting" ? "Submitting..." : "Submit"}
					</Button>
				</div>
			</DialogPopup>
		</Dialog>
	);
});
